// Weerbalk bovenaan Ideeën: weertype met subtiele animatie, temperatuur, wind, uitleg + advies,
// en de "Toch naar buiten"-schakelaar bij slecht weer. Zelfstandig (leest useWeather), dus herbruikbaar.
import {
  Cloud,
  CloudHail,
  CloudLightning,
  CloudOff,
  CloudRain,
  Footprints,
  Snowflake,
  Sun,
  Wind,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Pill } from '../../components/ui/Pill';
import { formatTemp, weatherAdvice, weatherCaption, windDisplay } from '../../domain/weather';
import type { WeatherKind } from '../../domain/types';
import { useToast, useWeather } from '../../state/AppProviders';
import './weather.css';

interface KindStyle {
  icon: LucideIcon;
  /** Oppervlak (licht + donker). */
  surface: string;
  /** Tekst (licht + donker). */
  text: string;
  /** Icoonkleur (licht + donker). */
  iconColor: string;
  /** Vlak achter het icoon. */
  iconBg: string;
}

const KIND_STYLES: Record<WeatherKind, KindStyle> = {
  zonnig: {
    icon: Sun,
    surface: 'bg-amber-100/85 dark:bg-amber-950/55',
    text: 'text-amber-950 dark:text-amber-50',
    iconColor: 'text-amber-600 dark:text-amber-300',
    iconBg: 'bg-amber-200/70 dark:bg-amber-900/60',
  },
  bewolkt: {
    icon: Cloud,
    surface: 'bg-slate-200/75 dark:bg-slate-800/65',
    text: 'text-slate-900 dark:text-slate-50',
    iconColor: 'text-slate-600 dark:text-slate-300',
    iconBg: 'bg-slate-300/60 dark:bg-slate-700/60',
  },
  regen: {
    icon: CloudRain,
    surface: 'bg-sky-100/85 dark:bg-sky-950/60',
    text: 'text-sky-950 dark:text-sky-50',
    iconColor: 'text-sky-700 dark:text-sky-300',
    iconBg: 'bg-sky-200/70 dark:bg-sky-900/60',
  },
  onweer: {
    icon: CloudLightning,
    surface: 'bg-indigo-100/85 dark:bg-indigo-950/60',
    text: 'text-indigo-950 dark:text-indigo-50',
    iconColor: 'text-indigo-700 dark:text-indigo-300',
    iconBg: 'bg-indigo-200/70 dark:bg-indigo-900/60',
  },
  hagel: {
    icon: CloudHail,
    surface: 'bg-slate-200/80 dark:bg-slate-800/70',
    text: 'text-slate-900 dark:text-slate-50',
    iconColor: 'text-sky-700 dark:text-sky-300',
    iconBg: 'bg-sky-100/80 dark:bg-slate-700/70',
  },
  storm: {
    icon: Wind,
    surface: 'bg-slate-300/60 dark:bg-slate-800/75',
    text: 'text-slate-900 dark:text-slate-50',
    iconColor: 'text-slate-700 dark:text-slate-200',
    iconBg: 'bg-slate-400/30 dark:bg-slate-700/70',
  },
  sneeuw: {
    icon: Snowflake,
    surface: 'bg-sky-50/90 dark:bg-slate-800/65',
    text: 'text-slate-900 dark:text-slate-50',
    iconColor: 'text-sky-600 dark:text-sky-200',
    iconBg: 'bg-sky-100/90 dark:bg-slate-700/60',
  },
  onbekend: {
    icon: CloudOff,
    surface: 'bg-slate-100/80 dark:bg-slate-900/65',
    text: 'text-slate-900 dark:text-slate-100',
    iconColor: 'text-slate-500 dark:text-slate-400',
    iconBg: 'bg-slate-200/70 dark:bg-slate-800/70',
  },
};

/** Decoratieve achtergrond per weertype (aria-hidden, staat stil bij reduced motion). */
function WeatherFx({ kind }: { kind: WeatherKind }) {
  const drops = (cls: string, count: number, color: string, size: string) =>
    Array.from({ length: count }, (_, i) => (
      <span
        key={`${cls}-${i}`}
        className={`absolute top-0 ${cls} ${color} ${size}`}
        style={{ left: `${8 + ((i * 83) % 88)}%`, animationDelay: `${(i * 0.37) % 1.6}s` }}
      />
    ));

  switch (kind) {
    case 'zonnig':
      return <span className="go-wx-glow absolute -right-10 -top-12 size-40 rounded-full bg-amber-300/40 blur-2xl dark:bg-amber-400/15" />;
    case 'bewolkt':
      return (
        <>
          <span className="go-wx-cloud absolute -right-6 top-2 h-12 w-32 rounded-full bg-white/50 blur-xl dark:bg-slate-500/20" />
          <span className="go-wx-cloud absolute right-24 -bottom-4 h-10 w-24 rounded-full bg-white/40 blur-xl dark:bg-slate-500/15" style={{ animationDelay: '-6s' }} />
        </>
      );
    case 'regen':
      return <>{drops('go-wx-drop', 7, 'bg-sky-500/35 dark:bg-sky-300/25', 'h-3 w-px rounded-full')}</>;
    case 'onweer':
      return (
        <>
          <span className="go-wx-sky-flash absolute inset-0 bg-white/60 dark:bg-indigo-200/15" />
          {drops('go-wx-drop', 5, 'bg-indigo-500/30 dark:bg-indigo-300/25', 'h-3 w-px rounded-full')}
        </>
      );
    case 'hagel':
      return <>{drops('go-wx-hail', 6, 'bg-slate-400/50 dark:bg-slate-300/30', 'size-1.5 rounded-full')}</>;
    case 'sneeuw':
      return <>{drops('go-wx-flake', 7, 'bg-sky-300/60 dark:bg-white/40', 'size-1.5 rounded-full')}</>;
    case 'storm':
      return (
        <>
          {[18, 48, 74].map((top, i) => (
            <span
              key={top}
              className="go-wx-streak absolute left-0 h-px w-16 rounded-full bg-slate-500/35 dark:bg-slate-300/25"
              style={{ top: `${top}%`, animationDelay: `${i * 0.8}s` }}
            />
          ))}
        </>
      );
    default:
      return null;
  }
}

export function WeatherBanner() {
  const { weather, overruled, setOverruled } = useWeather();
  const { showToast } = useToast();
  const style = KIND_STYLES[weather.kind] ?? KIND_STYLES.onbekend;
  const Icon = style.icon;

  const isSimulated = weather.source === 'simulatie';
  const isStale = weather.source === 'cache';
  const temp = formatTemp(weather.tempC);
  const windUnknown = weather.windKmh === null && (weather.source === 'onbekend' || weather.kind === 'onbekend');
  const wind = windDisplay(weather.windLevel, weather.windKmh, windUnknown);
  const statusText = overruled ? 'Toch naar buiten' : weather.isBadWeather ? 'Binnen aanbevolen' : 'Buiten & binnen';
  const caption = weatherCaption(weather, Date.now());
  const advice = weatherAdvice(weather, overruled);
  const showToggle = weather.isBadWeather || overruled;

  const toggle = () => {
    const next = !overruled;
    setOverruled(next);
    showToast(
      next
        ? 'Weerfilter genegeerd: buitenactiviteiten worden getoond'
        : 'Weerfilter hersteld: buitenactiviteiten die droog weer nodig hebben zijn verborgen',
    );
  };

  return (
    <section
      aria-label="Weerinformatie"
      className={`relative isolate overflow-hidden rounded-3xl p-4 backdrop-blur-md sm:p-5 ${style.surface} ${style.text} ${
        isStale ? 'saturate-[0.85]' : ''
      }`}
    >
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <WeatherFx kind={weather.kind} />
      </div>

      <div className="flex items-start gap-3.5">
        <div className={`flex size-14 shrink-0 items-center justify-center rounded-2xl ${style.iconBg}`}>
          <Icon size={30} strokeWidth={2} aria-hidden="true" className={`go-wx-icon-${weather.kind} ${style.iconColor}`} />
        </div>

        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
            <p className="text-lg font-bold leading-tight">
              {weather.label}
              {temp ? <span className="font-semibold opacity-80"> · {temp}</span> : null}
              {isSimulated ? <span className="sr-only"> (simulatie)</span> : null}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <Pill className="bg-black/5 dark:bg-white/10">{statusText}</Pill>
            <Pill icon={Wind} className="bg-black/5 dark:bg-white/10" title={wind.fullText}>
              {wind.label}
              {wind.speed !== null ? <span className="font-medium opacity-75">{wind.speed} km/u</span> : null}
            </Pill>
          </div>
        </div>
      </div>

      <div className="mt-3 space-y-0.5">
        <p className="text-xs font-semibold uppercase tracking-wide opacity-70">{caption}</p>
        <p className="text-sm leading-snug opacity-90">{advice}</p>
      </div>

      {showToggle ? (
        <button
          type="button"
          onClick={toggle}
          aria-pressed={overruled}
          className={`mt-3 inline-flex min-h-11 items-center gap-2 whitespace-nowrap rounded-2xl px-4 text-sm font-semibold transition-all duration-150 active:scale-95 ${
            overruled
              ? 'bg-slate-900 text-slate-50 dark:bg-slate-100 dark:text-slate-900'
              : 'bg-black/5 hover:bg-black/10 dark:bg-white/10 dark:hover:bg-white/15'
          }`}
        >
          {overruled ? <X size={16} aria-hidden="true" /> : <Footprints size={16} aria-hidden="true" />}
          {overruled ? 'Weerfilter weer aanzetten' : 'Toch naar buiten'}
        </button>
      ) : null}
    </section>
  );
}
